import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { createProfileSchema } from "../schema/profile.schema";
import type { ProfileFormData, ProfileData } from "../types";
import { useUpdateProfile } from "../services/UpdateProfile";
import { useUpdateProfileImage } from "../services/UpdateImage";
import { showNotification } from "@libs/notistack/notificationStack";
import { SNACKBAR_SEVERITY } from "@constants/notificationStack";
import { useLogout } from "@hooks/useLogout";
import { uploadAttachment } from "@utils/attachmentUpload";
import { FILE_UPLOAD_CONFIGS } from "@utils/fileUploadUtils";

/**
 * Convert base64 data URL to File object
 */
const dataURLtoFile = (dataUrl: string, filename: string): File => {
	const arr = dataUrl.split(",");
	const mime = arr[0].match(/:(.*?);/)?.[1] || "image/png";
	const bstr = atob(arr[1]);
	let n = bstr.length;
	const u8arr = new Uint8Array(n);
	while (n--) {
		u8arr[n] = bstr.charCodeAt(n);
	}
	return new File([u8arr], filename, { type: mime });
};

export const useProfileForm = (
	data: ProfileData | undefined,
	t: (key: string) => string,
	onProfileUpdateSuccess?: () => void,
) => {
	const [isEditMode, setIsEditMode] = useState<boolean>(false);
	const [showEmailChangeWarning, setShowEmailChangeWarning] = useState<boolean>(false);
	const [pendingFormData, setPendingFormData] = useState<ProfileFormData | null>(null);
	const [isUploadingSignature, setIsUploadingSignature] = useState<boolean>(false);
	const [pendingSignatureDataUrl, setPendingSignatureDataUrl] = useState<string | null>(null);
	const [uploadedSignatureKey, setUploadedSignatureKey] = useState<string | null>(null);
	const { mutate: updateProfile, isPending: isUpdating } = useUpdateProfile();
	const { mutate: updateProfileImage, isPending: isUploading } = useUpdateProfileImage();
	const { handleLogoutConfirm } = useLogout();

	const profileSchema = createProfileSchema(t);
	const form = useForm<ProfileFormData>({
		resolver: zodResolver(profileSchema),
		defaultValues: {
			firstName: data?.firstName || "",
			lastName: data?.lastName || "",
			phoneNumber: data?.phoneNumber || "",
			emailAddress: data?.emailAddress || "",
		},
	});

	// Debug: Log form errors when they change
	useEffect(() => {
		if (Object.keys(form.formState.errors).length > 0) {
			console.log("Form validation errors:", form.formState.errors);
		}
	}, [form.formState.errors]);

	// Reset form when data changes (e.g., after successful update)
	useEffect(() => {
		if (data) {
			form.reset({
				firstName: data.firstName || "",
				lastName: data.lastName || "",
				phoneNumber: data.phoneNumber || "",
				emailAddress: data.emailAddress || "",
			});
		}
	}, [data, form]);

	const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
		const file = event.target.files?.[0];
		if (file) {
			updateProfileImage(file);
		}
	};

	/**
	 * Handle signature from canvas - stores base64 data URL locally (doesn't upload yet)
	 */
	const handleSignatureChange = (dataUrl: string | null): void => {
		console.log("=== SIGNATURE CHANGE ===", dataUrl ? "Signature drawn, will upload on Save" : "Clearing signature");

		// Store the data URL locally - will be uploaded when Save is clicked
		setPendingSignatureDataUrl(dataUrl);
		setUploadedSignatureKey(null); // Clear any previously uploaded signature
	};

	const onSubmit = async (formData: ProfileFormData): Promise<void> => {
		console.log("=== PROFILE FORM SUBMIT ===");
		console.log("Form data:", formData);
		console.log("Original data:", data);
		console.log("Pending signature:", pendingSignatureDataUrl ? "Yes" : "No");

		// Check if signature needs to be uploaded
		const hasSignatureChange = pendingSignatureDataUrl !== null;
		const hasOtherChanges =
			formData.firstName !== data?.firstName ||
			formData.lastName !== data?.lastName ||
			formData.emailAddress !== data?.emailAddress ||
			formData.phoneNumber !== data?.phoneNumber;

		const hasChanges = hasOtherChanges || hasSignatureChange;

		console.log("Has changes:", hasChanges);
		console.log("Field changes:", {
			firstName: formData.firstName !== data?.firstName,
			lastName: formData.lastName !== data?.lastName,
			email: formData.emailAddress !== data?.emailAddress,
			phone: formData.phoneNumber !== data?.phoneNumber,
			signature: hasSignatureChange,
		});

		if (!hasChanges) {
			console.log("No changes detected, not submitting");
			setIsEditMode(false);
			return;
		}

		// Check if email has changed
		const emailChanged = formData.emailAddress !== data?.emailAddress;

		let signatureKey: string | null = null;

		// Upload signature first if there's a pending one
		if (pendingSignatureDataUrl) {
			try {
				setIsUploadingSignature(true);
				console.log("Uploading signature...");

				// Convert base64 data URL to File
				const timestamp = Date.now();
				const file = dataURLtoFile(pendingSignatureDataUrl, `signature_${timestamp}.png`);

				// Upload signature using the standard attachment upload utility
				signatureKey = await uploadAttachment(file, FILE_UPLOAD_CONFIGS.PROFILE_PICTURE);
				console.log("Signature upload successful, objectKey:", signatureKey);

				setUploadedSignatureKey(signatureKey);
				setPendingSignatureDataUrl(null);
			} catch (error) {
				console.error("Signature upload failed:", error);
				setIsUploadingSignature(false);
				showNotification({
					message: t("psdaCommon.profile.signature.uploadError") || "Failed to upload signature",
					severity: SNACKBAR_SEVERITY.ERROR,
				});
				return;
			} finally {
				setIsUploadingSignature(false);
			}
		}

		// Prepare payload with signature if uploaded
		const payload = {
			...formData,
			...(signatureKey && { signature: signatureKey }),
		};

		if (emailChanged) {
			// Show warning for email change
			setPendingFormData(payload);
			setShowEmailChangeWarning(true);
			return;
		}

		console.log("=== CALLING UPDATE PROFILE API ===");
		console.log("Payload:", payload);
		const hasSignatureUpdate = !!signatureKey;

		updateProfile(payload, {
			onSuccess: response => {
				console.log("Profile update successful:", response);
				setIsEditMode(false);
				setUploadedSignatureKey(null); // Clear the uploaded signature key
				setPendingSignatureDataUrl(null); // Clear pending signature

				// If signature was uploaded, close the modal (profile is complete)
				if (hasSignatureUpdate && onProfileUpdateSuccess) {
					onProfileUpdateSuccess();
				}
			},
			onError: error => {
				console.error("Profile update failed:", error);
			},
		});
	};

	const handleCancel = (): void => {
		setIsEditMode(false);
		setUploadedSignatureKey(null); // Clear uploaded signature on cancel
		setPendingSignatureDataUrl(null); // Clear pending signature on cancel
		form.reset({
			firstName: data?.firstName || "",
			lastName: data?.lastName || "",
			emailAddress: data?.emailAddress || "",
			phoneNumber: data?.phoneNumber || "",
		});
	};

	const handleEdit = (): void => {
		setIsEditMode(true);
		// Reset form to current data when entering edit mode
		form.reset({
			firstName: data?.firstName || "",
			lastName: data?.lastName || "",
			emailAddress: data?.emailAddress || "",
			phoneNumber: data?.phoneNumber || "",
		});
	};

	const handleEmailChangeConfirm = (): void => {
		if (!pendingFormData) return;

		// Show notification about email change
		showNotification({
			title: t("psdaCommon.profile.emailChangeWarningModal.modalTitle"),
			message: t("psdaCommon.profile.emailChangeWarningModal.notificationMessage"),
			severity: SNACKBAR_SEVERITY.WARNING,
			autoHideDuration: 5000,
		});

		// Update profile with new email
		updateProfile(pendingFormData, {
			onSuccess: response => {
				console.log("Profile update successful:", response);
				setIsEditMode(false);
				setShowEmailChangeWarning(false);
				setPendingFormData(null);

				// Logout user after successful email update
				setTimeout(() => {
					handleLogoutConfirm();
				}, 2000); // Give user time to see the success message
			},
			onError: error => {
				console.error("Profile update failed:", error);
				setShowEmailChangeWarning(false);
				setPendingFormData(null);
			},
		});
	};

	const handleEmailChangeCancel = (): void => {
		setShowEmailChangeWarning(false);
		setPendingFormData(null);
	};

	return {
		isEditMode,
		isUpdating,
		isUploading,
		isUploadingSignature,
		uploadedSignatureKey,
		pendingSignatureDataUrl,
		form,
		handleImageChange,
		handleSignatureChange,
		onSubmit,
		handleCancel,
		handleEdit,
		showEmailChangeWarning,
		handleEmailChangeConfirm,
		handleEmailChangeCancel,
	};
};
